import { useEffect, useMemo, useRef, useState } from 'react'

import { useDbValue } from '../lib/useDbValue.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'
import { setCalcuttaBid, setCalcuttaLive, finishCalcuttaFlight } from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'
import { parseCalcuttaLine, isFinishCommand } from '../lib/calcuttaVoice.js'
import { useSpeech } from '../lib/useSpeech.js'
import CLUB from '../config/club.js'

// Phone remote for the roaming auction clerk. Gated by the SAME 2026 code, scoped
// to the current/active tournament (resolved like the AI/TV/gate). ENTRY ONLY —
// bid / owner / on-block / Save / Finish, Speak or Tap. It writes to the same
// calcutta node as the PC, so entries reflect instantly on the PC + TVs. It never
// controls TVs or settings, and MGA never appears here.
const CODE = '2026'
const GREEN = 'var(--ge-primary)', GOLD = 'var(--ge-gold)', CREAM = 'var(--ge-cream-premium)'

export default function CalcuttaRemote() {
  const [unlocked, setUnlocked] = useState(() => { try { return sessionStorage.getItem('calcutta_remote_ok') === '1' } catch { return false } })
  if (!unlocked) {
    return <Gate onUnlock={() => { try { sessionStorage.setItem('calcutta_remote_ok', '1') } catch { /* ignore */ } setUnlocked(true) }} />
  }
  return <Remote />
}

function Gate({ onUnlock }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const submit = (e) => { e?.preventDefault?.(); if (code.trim() === CODE) onUnlock(); else setErr('Wrong code.') }
  return (
    <div style={{ minHeight: '100vh', background: GREEN, color: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui, sans-serif' }}>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>🎙️</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: '4px 0' }}>Calcutta Remote</h1>
        <p style={{ opacity: 0.8, fontSize: 14, marginBottom: 16 }}>Enter the access code.</p>
        <input type="password" inputMode="numeric" autoFocus value={code} onChange={(e) => { setCode(e.target.value); setErr('') }}
          placeholder="Access code"
          style={{ width: '100%', textAlign: 'center', fontSize: 22, fontWeight: 800, letterSpacing: 4, padding: '12px', borderRadius: 12, border: 'none', boxSizing: 'border-box', color: '#111', background: '#fff', WebkitTextFillColor: '#111', caretColor: '#111' }} />
        {err ? <p style={{ color: '#FCA5A5', marginTop: 8, fontWeight: 700 }}>{err}</p> : null}
        <button type="submit" style={{ marginTop: 14, width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: GOLD, color: GREEN, fontWeight: 900, fontSize: 17 }}>Enter →</button>
      </form>
    </div>
  )
}

function Remote() {
  const { data: all } = useDbValue('tournaments')
  const tid = useMemo(() => pickPremiumCurrentEventId(all || {}), [all])
  const { data: tournament } = useDbValue(tid ? `tournaments/${tid}` : null)
  const cal = tournament?.calcutta || {}
  const live = cal.live || null
  const bids = cal.bids || {}
  const flights = useMemo(() => (tournament?.flights ? Object.entries(tournament.flights).map(([id, f]) => ({ id, ...f })) : []), [tournament])
  const flight = (live?.flightId && flights.find((f) => f.id === live.flightId)) || flights[0] || null
  const teams = useMemo(() => (flight ? Object.entries(flight.teams || {}).map(([id, t]) => ({ id, name: lastNames(t.info?.name) || id, players: t.info?.players || [] })) : []), [flight])
  const idx = teams.findIndex((t) => t.id === live?.teamId)
  const curIdx = idx >= 0 ? idx : 0
  const team = teams[curIdx] || null
  const currentTeamId = team?.id || null

  const [bid, setBid] = useState('')
  const [owner, setOwner] = useState('')
  const [msg, setMsg] = useState('')
  // Snapshot of the last submitted sale for THIS team — drives the green
  // confirmation banner. Cleared when the team changes or the fields are edited.
  const [confirmed, setConfirmed] = useState(null)
  const pendingRef = useRef(null)
  useEffect(() => {
    setConfirmed(null)
    if (pendingRef.current && pendingRef.current.teamId === currentTeamId) {
      const p = pendingRef.current; pendingRef.current = null
      setBid(p.bid != null ? String(p.bid) : ''); setOwner(p.owner || ''); return
    }
    const b = bids[currentTeamId]
    setBid(b?.bidAmount ? String(b.bidAmount) : ''); setOwner(b?.buyerName || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId])

  const soldCount = teams.filter((t) => Number(bids[t.id]?.bidAmount) > 0).length
  const allSold = teams.length > 0 && soldCount === teams.length

  // Step 1: record the sale and confirm it on screen — does NOT advance.
  const submit = async () => {
    if (!tid || !currentTeamId) return
    const snap = { teamName: team.name, owner: owner.trim(), bid: Number(bid) || 0 }
    await setCalcuttaBid(tid, currentTeamId, { buyerName: snap.owner, bidAmount: snap.bid })
    setConfirmed(snap)
    setMsg('')
  }
  // Step 2: advance to the next team (a separate, deliberate tap).
  const nextTeam = () => {
    const next = teams[curIdx + 1]
    setConfirmed(null)
    if (next) setCalcuttaLive(tid, { flightId: flight.id, teamId: next.id })
  }
  const applyLine = (text) => {
    const line = String(text || '').trim(); if (!line) return
    setConfirmed(null)
    if (isFinishCommand(line)) { if (allSold) finishCalcuttaFlight(tid, flight.id); return }
    const r = parseCalcuttaLine(line, teams)
    if (r.teamId && r.teamId !== currentTeamId) { pendingRef.current = { teamId: r.teamId, bid: r.bid, owner: r.owner }; setCalcuttaLive(tid, { flightId: flight.id, teamId: r.teamId }) }
    else { if (r.bid != null) setBid(String(r.bid)); if (r.owner) setOwner(r.owner) }
    setMsg(`Heard: ${r.matchedName || '?'} · ${r.owner || '?'} · ${r.bid != null ? '$' + r.bid : '?'}`)
  }
  const speech = useSpeech({ onResult: applyLine })

  if (!tid || !tournament) return <Shell><p style={{ opacity: 0.85 }}>Waiting for the tournament — build the field to start the auction.</p></Shell>
  if (!flight || !team) return <Shell><p style={{ opacity: 0.85 }}>Waiting for the auction to start on the PC…</p></Shell>

  const canSubmit = Number(bid) > 0 && owner.trim().length > 0
  const hasNext = curIdx < teams.length - 1
  return (
    <Shell>
      <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.7, fontWeight: 800 }}>{flight.info?.name} · {curIdx + 1} of {teams.length} · {soldCount} sold</div>
      <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1.05, margin: '6px 0' }}>{team.name}</div>
      {team.players.length ? <div style={{ opacity: 0.75, fontSize: 15 }}>{team.players.join(' · ')}</div> : null}

      <label style={{ display: 'block', marginTop: 18 }}>
        <span style={lbl}>Winning Bid</span>
        <input inputMode="numeric" pattern="[0-9]*" value={bid} onChange={(e) => { setConfirmed(null); setBid(e.target.value.replace(/[^0-9]/g, '').slice(0, 7)) }} placeholder="$" style={fld} />
      </label>
      <label style={{ display: 'block', marginTop: 12 }}>
        <span style={lbl}>Owner</span>
        <input value={owner} onChange={(e) => { setConfirmed(null); setOwner(e.target.value) }} placeholder="Name" autoCapitalize="words" style={fld} />
      </label>

      {confirmed ? (
        <>
          <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 14, background: '#127A3E', color: '#fff', fontWeight: 800, fontSize: 17, textAlign: 'center', lineHeight: 1.3 }}>
            ✓ Sold — {confirmed.teamName} · {confirmed.owner || '—'} · ${confirmed.bid.toLocaleString()}
          </div>
          <button onClick={nextTeam} style={{ ...bigBtn }}>{hasNext ? 'Next team →' : 'Done'}</button>
        </>
      ) : (
        <button onClick={submit} disabled={!canSubmit} style={{ ...bigBtn, opacity: canSubmit ? 1 : 0.5 }}>Submit</button>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        {speech.supported ? (
          <button onClick={() => (speech.listening ? speech.stop() : speech.start())}
            style={{ ...miniBtn, background: speech.listening ? '#B10C0C' : GOLD, color: speech.listening ? '#fff' : GREEN, flex: 1 }}>
            {speech.listening ? '● Listening…' : '🎤 Speak'}
          </button>
        ) : null}
        <button onClick={() => curIdx < teams.length - 1 && setCalcuttaLive(tid, { flightId: flight.id, teamId: teams[curIdx + 1].id })} style={{ ...miniBtn, flex: 1 }}>Skip ›</button>
      </div>
      {allSold ? <button onClick={() => finishCalcuttaFlight(tid, flight.id)} style={{ ...miniBtn, width: '100%', marginTop: 10, background: 'rgba(247,242,230,0.06)' }}>Finish Flight</button> : null}
      {msg ? <p style={{ opacity: 0.7, fontSize: 13, marginTop: 10, textAlign: 'center' }}>{msg}</p> : null}
      {speech.transcript ? <p style={{ opacity: 0.55, fontSize: 12, marginTop: 6, textAlign: 'center' }}>“{speech.transcript}”</p> : null}
      <p style={{ opacity: 0.45, fontSize: 11, marginTop: 14, textAlign: 'center' }}>Say “Team, Owner, Bid” — e.g. “Langford Gunter, Bob Anderson, forty-two hundred”. TVs &amp; settings stay on the PC.</p>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: GREEN, color: CREAM, fontFamily: 'system-ui, sans-serif', padding: '20px 16px 40px' }}>
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          {CLUB.logoLight ? <img src={CLUB.logoLight} alt="" style={{ height: 46 }} /> : null}
          <div style={{ color: GOLD, fontWeight: 900, letterSpacing: 3, fontSize: 12 }}>CALCUTTA REMOTE</div>
        </div>
        {children}
      </div>
    </div>
  )
}

const lbl = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7, fontWeight: 800 }
const fld = { width: '100%', marginTop: 4, fontSize: 24, fontWeight: 800, padding: '12px', borderRadius: 12, border: 'none', boxSizing: 'border-box', color: '#111', background: '#fff', WebkitTextFillColor: '#111', caretColor: '#111' }
const bigBtn = { width: '100%', marginTop: 18, padding: '18px', borderRadius: 14, border: 'none', background: GOLD, color: GREEN, fontWeight: 900, fontSize: 20 }
const miniBtn = { padding: '12px', borderRadius: 12, border: `1px solid ${GOLD}66`, background: 'rgba(247,242,230,0.08)', color: CREAM, fontWeight: 800, fontSize: 15 }
